import { useLayoutEffect, useRef, useState } from 'react'
import { cx } from './cx'
import s from './Steps.module.css'

export interface Step {
  key: string
  label: string
  /** optional short caption under the label */
  hint?: string
  /** mark this step complete regardless of position (a checkmark shows) */
  done?: boolean
}

/**
 * A horizontal stepper (the Kendo Stepper of the kit) — the progress spine of any
 * wizard: numbered nodes joined by a connector, with done / active / upcoming
 * states. Controlled: the parent owns `current` (index) and the step list; the
 * stepper renders the rail and (if `onStep` is given) lets the user jump to an
 * already-reached step. It's the chrome, not the flow — pair it with your own
 * body + Back/Next buttons (see the Setup wizard in the gallery).
 *
 * ── `compact` — the nodes without their words on screen (#2406) ───────────
 * A dot beside every label needs roughly 300px for three short steps, and a
 * phone dialog's header at 320px (the 200%-zoom equivalent) has about 230 once
 * it has a close button. The relationship workspace drew its own three
 * numbered dots and a "Step N of M" line for exactly that reason — a second
 * stepper that had no done state and nothing a screen reader could walk.
 *
 * Compact keeps the nodes, their states and the connector, and takes the
 * LABELS out of the picture but not out of the accessibility tree: each node
 * still reads "1 Relationship type", the current one still carries
 * `aria-current="step"`. The host says where the person is in words beside
 * the rail, because a caption is copy and copy is the host's.
 *
 * ── no `onStep`, no buttons ────────────────────────────────────────────────
 * A stepper nobody can navigate used to render every node as a DISABLED
 * `<button>`: three controls that exist only to be refused, announced as
 * "dimmed button", and counted by every target-size sweep as a 22px control
 * under the pointer floor (`relationship-picker-touch.spec.ts` test 9 found
 * exactly that the moment the workspace adopted this). `StatusRail` already
 * rules the other way — "absent ⇒ the node is a `<span>`, not a button. The
 * affordance is a prop." — and so does this now. With `onStep`, nodes are
 * buttons, and the ones not yet reached are disabled, as before.
 *
 * ── a horizontal rail that does not fit FITS ITSELF (#2406 b3-controls) ────
 * Every node kept its label on one line and nothing ever gave way, so six
 * steps drew a 666px rail in a 350px phone column: "Branding", "Modules" and
 * "Done" hung off the page (rogue census, control-clipped, /ui). The width a
 * rail needs is its words, which no stylesheet can know in advance, so the
 * rail MEASURES: when its content is wider than its box it draws the compact
 * nodes — every state, every connector, every label still its node's
 * accessible name — and keeps the CURRENT step's label on screen, because
 * without a host caption (which `compact` hosts supply) the words for where the
 * person is would otherwise be gone. It returns to the full rail as soon as
 * the box can hold the width it measured. `compact` and `vertical` rails are
 * left alone: the first already fits by choice, the second cannot overflow
 * sideways.
 */
type StepState = 'done' | 'active' | 'upcoming'

/**
 * `true` while the rail's full, labelled drawing is wider than its box. The
 * natural width is recorded when it is measured, because once fitted the rail
 * is narrow and can no longer say how wide it would be. Re-measured from the
 * full drawing whenever the words change.
 */
function useFitted(enabled: boolean, words: string) {
  const rail = useRef<HTMLOListElement>(null)
  const [fitted, setFitted] = useState(false)
  const natural = useRef(0)
  const now = useRef(false)
  useLayoutEffect(() => {
    const el = rail.current
    now.current = false
    natural.current = 0
    setFitted(false)
    if (!enabled || !el || typeof ResizeObserver === 'undefined') return
    const check = () => {
      if (!now.current) {
        if (el.scrollWidth > el.clientWidth + 1) {
          natural.current = el.scrollWidth
          now.current = true
          setFitted(true)
        }
      } else if (el.clientWidth >= natural.current) {
        now.current = false
        setFitted(false)
      }
    }
    check()
    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [enabled, words])
  return [rail, fitted] as const
}

function stateOf(steps: Step[], current: number, i: number): StepState {
  return steps[i]?.done || i < current ? 'done' : i === current ? 'active' : 'upcoming'
}

export function Steps({
  steps, current, onStep, className, vertical = false, compact = false,
}: {
  steps: Step[]
  current: number
  onStep?: (index: number) => void
  className?: string
  vertical?: boolean
  /** Nodes only on screen; every label stays in the accessible name. */
  compact?: boolean
}) {
  const [rail, fitted] = useFitted(!vertical && !compact,
    steps.map((st) => `${st.label}\u0000${st.hint ?? ''}`).join('\u0001'))
  return (
    <ol ref={rail} className={cx(s.rail, vertical ? s.vertical : s.horizontal, (compact || fitted) && s.compact,
      fitted && s.fitted, className)} data-fitted={fitted || undefined}>
      {steps.map((st, i) => {
        const state = stateOf(steps, current, i)
        const reachable = !!onStep && (state === 'done' || i <= current)
        const face = (
          <>
            <span className={s.dot}>{state === 'done' ? '✓' : i + 1}</span>
            <span className={s.text}>
              <span className={s.label}>{st.label}</span>
              {st.hint && <span className={s.hint}>{st.hint}</span>}
            </span>
          </>
        )
        return (
          <li key={st.key} className={cx(s.step, s[state])} data-testid={`step-${st.key}`} data-state={state}
            aria-current={state === 'active' ? 'step' : undefined}>
            {onStep ? (
              <button type="button" className={s.node} disabled={!reachable}
                onClick={reachable ? () => onStep(i) : undefined}>
                {face}
              </button>
            ) : (
              <span className={s.node}>{face}</span>
            )}
            {i < steps.length - 1 && (
              /* COMPLETE only when BOTH ends are reached: this step is done and
                 the next one is done or current. A step marked `done` out of
                 order used to draw a finished line into a step nobody had
                 reached (#2406 kit-defects-b). */
              <span className={s.connector} aria-hidden="true"
                data-complete={state === 'done' && stateOf(steps, current, i + 1) !== 'upcoming' ? '' : undefined} />
            )}
          </li>
        )
      })}
    </ol>
  )
}
